import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { notifyRbacUpdated } from './rbac';
import { getBrand } from './brand';
import { useLanguage } from '../i18n/LanguageContext';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';

const RESTAURANT_PROPERTY = 'R';
// Domain decides the login logo: restaurant host → DineMaster (222.png),
// hotel host → Hotel Desk (LOGO 12 / hoteldesk.png).
const BRAND = getBrand();

/** Matches account/init: propertType, PropertyType, propertyType */
const normalizePropertyType = (data) => {
  const v =
    data?.propertType ?? data?.PropertyType ?? data?.propertyType ?? '';
  return String(v).trim().toUpperCase();
};

const isRestaurantProperty = (data) =>
  normalizePropertyType(data) === RESTAURANT_PROPERTY;

/** So /setup → HotelSetupWizard → RestaurantSetupWizard (isRestaurantCompany). */
const withRestaurantCompanyDetails = (data) => {
  const details = { ...data };
  const pt = normalizePropertyType(data);
  if (pt) details.propertType = pt;
  if (isRestaurantProperty(data)) {
    details.isRestaurant = true;
  }
  return details;
};

function extractHotels(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  for (const key of ['data', 'hotels', 'result', 'results', 'items', 'value']) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  if (raw.id || raw.Id || raw.hotelId || raw.HotelId) return [raw];
  return [];
}

// ── Login errors the server cannot see ──────────────────────────────────────
//
// The API logs every login it refuses (L01-L05, L07, L08). What it can NOT log
// is a request that never reached it - no internet, a network or VPN that
// blocks us, a server too slow to answer. Those are worked out here, shown to
// the person in plain words with a code, and KEPT ON THE PHONE until the phone
// reaches the API again, then sent to api/account/loginerror. Same for a
// company code that matched nothing (L02), the company lookup itself failing
// on the server (L09), and the app failing to open after a good login (L06).
//
// Never the password - only the code, what the person saw, what the browser
// itself said, the company code and the username as typed.

const REQUEST_TIMEOUT_MS = 30000;
const LOGIN_ERROR_QUEUE_KEY = 'pendingLoginErrors';
// "Remember me": the company code and username only - never the password.
const REMEMBER_KEY = 'rememberLogin';

const NETWORK_MESSAGES = {
  'L00-OFFLINE':
    'No internet connection. Please check your internet and try again.',
  'L00-BLOCKED':
    'Unable to connect to the server. Please try another network, or turn off any VPN / ad-blocker, and try again.',
  'L00-TIMEOUT':
    'The server is taking too long to respond. Please check your internet and try again.',
};

// A fetch that gives up after REQUEST_TIMEOUT_MS, so a request hanging on a
// weak network ends in a clear "too slow" instead of a spinner forever.
async function fetchWithTimeout(url, options = {}, ms = REQUEST_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Why a request never got an answer. The browser does not say why a fetch
// failed, so this goes by what it can know: the phone's own online flag, and
// whether our timeout is what stopped it. Anything else - internet on, no
// answer - is a network, VPN, DNS or ad-blocker standing in the way.
const networkCodeFor = (err) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false)
    return 'L00-OFFLINE';
  if (err?.name === 'AbortError') return 'L00-TIMEOUT';
  return 'L00-BLOCKED';
};

// Local clock time without a zone, which is how the server stores times.
const localStamp = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 19);
};

// Storage can throw (private mode, blocked site data) - a report that cannot
// be kept is lost quietly rather than breaking the login screen.
const readLoginErrorQueue = () => {
  try {
    const v = JSON.parse(localStorage.getItem(LOGIN_ERROR_QUEUE_KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

const writeLoginErrorQueue = (list) => {
  try {
    if (list.length)
      localStorage.setItem(LOGIN_ERROR_QUEUE_KEY, JSON.stringify(list.slice(-20)));
    else localStorage.removeItem(LOGIN_ERROR_QUEUE_KEY);
  } catch {
    /* nowhere to keep it */
  }
};

let flushingLoginErrors = false;

// Sends whatever is waiting. Called whenever this page has just heard from the
// API - that is the proof the phone can reach it now.
async function flushLoginErrors() {
  if (flushingLoginErrors) return;
  const items = readLoginErrorQueue();
  if (!items.length) return;
  flushingLoginErrors = true;
  try {
    const r = await fetchWithTimeout(
      `${config.API_BASE_URL}/account/loginerror`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      },
      15000,
    );
    // Only what was sent comes off - anything queued meanwhile stays.
    if (r.ok) writeLoginErrorQueue(readLoginErrorQueue().slice(items.length));
  } catch {
    /* still cannot reach us - it waits for the next time */
  } finally {
    flushingLoginErrors = false;
  }
}

// Keep it, then try to send it straight away.
function reportLoginError(item) {
  writeLoginErrorQueue([
    ...readLoginErrorQueue(),
    { ...item, occurredAt: localStamp() },
  ]);
  flushLoginErrors();
}

// What "Remember me" kept last time, if anything.
const readRemembered = () => {
  try {
    const v = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null');
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
};

// ── The page's own styles ───────────────────────────────────────────────────
// Plain CSS so the two panels, the breakpoints and the hover states live in
// one place. Prefixed ld- so nothing leaks into the admin shell.
const STYLES = `
  .ld-root { min-height: 100vh; display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif; background: #FFFFFF; color: #0F172A; }
  .ld-root * { box-sizing: border-box; }

  /* ── left: the hero ── */
  .ld-hero { position: relative; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; padding: 40px 48px; color: #fff; min-height: 100vh;
    background: linear-gradient(180deg, #0B1B3A 0%, #0E2452 55%, #10305F 100%); }
  .ld-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; opacity: 0.55; }
  .ld-hero-skyline { position: absolute; left: 0; right: 0; bottom: 0; height: 58%; pointer-events: none; opacity: 0.35;
    background:
      linear-gradient(180deg, rgba(11,27,58,0) 0%, rgba(11,27,58,0.9) 100%),
      repeating-linear-gradient(90deg, rgba(255,255,255,0.0) 0 22px, rgba(255,214,150,0.16) 22px 30px, rgba(255,255,255,0.0) 30px 44px),
      repeating-linear-gradient(0deg, rgba(255,255,255,0.0) 0 18px, rgba(255,214,150,0.12) 18px 24px, rgba(255,255,255,0.0) 24px 36px);
    clip-path: polygon(0 40%, 14% 40%, 14% 18%, 30% 18%, 30% 32%, 48% 32%, 48% 8%, 64% 8%, 64% 26%, 80% 26%, 80% 44%, 100% 44%, 100% 100%, 0 100%); }
  .ld-hero-shade { position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(90deg, rgba(8,20,45,0.92) 0%, rgba(8,20,45,0.72) 45%, rgba(8,20,45,0.45) 100%),
                radial-gradient(60% 50% at 10% 100%, rgba(226,58,78,0.28), transparent 70%); }
  .ld-hero > * { position: relative; }

  .ld-logo { display: flex; align-items: center; gap: 10px; }
  .ld-logo-mark { width: 38px; height: 38px; border-radius: 9px; background: #D71F2B; display: flex; align-items: center; justify-content: center; font-weight: 900; font-size: 24px; color: #fff; letter-spacing: -1px; box-shadow: 0 6px 16px rgba(215,31,43,0.35); }
  .ld-logo-text { line-height: 1; }
  .ld-logo-text .ld-l1 { font-weight: 800; font-size: 15px; letter-spacing: 0.02em; }
  .ld-logo-text .ld-l2 { font-weight: 800; font-size: 15px; letter-spacing: 0.02em; }
  .ld-logo-text .ld-l3 { font-size: 9.5px; font-weight: 500; opacity: 0.85; margin-top: 3px; }
  .ld-logo.ld-on-white .ld-l1 { color: #0F172A; }
  .ld-logo.ld-on-white .ld-l2 { color: #D71F2B; }
  .ld-logo.ld-on-white .ld-l3 { color: #6B7280; }

  .ld-eyebrow { display: flex; align-items: center; gap: 10px; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; color: #C7D2E6; margin-top: 44px; }
  .ld-eyebrow::before { content: ''; width: 22px; height: 2px; background: #E23A4E; border-radius: 2px; }
  .ld-h1 { font-size: clamp(30px, 3.4vw, 44px); line-height: 1.12; font-weight: 800; letter-spacing: -0.02em; margin: 14px 0 0; }
  .ld-h1 .ld-accent { color: #E23A4E; }
  .ld-lead { font-size: 14.5px; line-height: 1.6; color: #C7D2E6; max-width: 440px; margin: 14px 0 0; }

  .ld-features { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 28px; margin-top: 34px; max-width: 560px; }
  .ld-feature { display: flex; align-items: flex-start; gap: 12px; }
  .ld-feature-ico { width: 34px; height: 34px; border-radius: 9px; border: 1px solid rgba(255,255,255,0.22); background: rgba(255,255,255,0.06); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ld-feature-ico svg { font-size: 19px; color: #fff; }
  .ld-feature b { display: block; font-size: 13px; font-weight: 700; }
  .ld-feature span { display: block; font-size: 11.5px; color: #AEBBD3; margin-top: 2px; }

  .ld-foot { margin-top: 36px; font-size: 11.5px; color: #AEBBD3; line-height: 1.7; }
  .ld-foot b { color: #fff; font-weight: 700; }

  /* ── right: the form ── */
  .ld-side { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 32px 32px; min-height: 100vh; }
  .ld-lang { position: absolute; top: 18px; right: 22px; }
  .ld-lang-btn { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 10px 0 10px; border-radius: 8px; border: 1px solid #E5E7EB; background: #fff; color: #374151; font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .ld-lang-btn svg { font-size: 16px; color: #6B7280; }
  .ld-lang-menu { position: absolute; right: 0; top: 38px; width: 170px; background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; box-shadow: 0 12px 32px rgba(15,23,42,0.12); padding: 6px; z-index: 5; }
  .ld-lang-item { display: flex; align-items: center; justify-content: space-between; padding: 8px 10px; border-radius: 7px; font-size: 13px; color: #111827; cursor: pointer; }
  .ld-lang-item:hover { background: #F3F4F6; }
  .ld-lang-item.ld-on { color: #D71F2B; font-weight: 700; }

  .ld-card { width: 100%; max-width: 400px; }
  .ld-card .ld-logo { justify-content: center; margin-bottom: 22px; }
  .ld-title { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; text-align: center; margin: 0; color: #0F172A; }
  .ld-sub { text-align: center; color: #6B7280; font-size: 13.5px; margin: 6px 0 26px; }

  .ld-field { margin-bottom: 16px; }
  .ld-label { display: flex; align-items: center; gap: 5px; font-size: 12.5px; font-weight: 600; color: #111827; margin-bottom: 7px; }
  .ld-label svg { font-size: 14px; color: #9CA3AF; }
  .ld-input { position: relative; }
  .ld-input > svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 19px; color: #9CA3AF; pointer-events: none; }
  .ld-input input { width: 100%; height: 46px; padding: 0 44px 0 40px; border-radius: 10px; border: 1.5px solid #E5E7EB; background: #FFFFFF; font-size: 16px; color: #111827; outline: none; font-family: inherit; transition: border-color .15s, box-shadow .15s; }
  .ld-input input::placeholder { color: #9CA3AF; font-size: 14px; }
  .ld-input input:focus { border-color: #D71F2B; box-shadow: 0 0 0 3px rgba(215,31,43,0.12); }
  .ld-input.ld-err input { border-color: #DC2626; }
  .ld-eye { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); width: 34px; height: 34px; border: none; background: none; cursor: pointer; color: #6B7280; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
  .ld-eye:hover { background: #F3F4F6; }
  .ld-eye svg { font-size: 20px; }
  .ld-help { font-size: 11.5px; color: #9CA3AF; margin-top: 6px; }
  .ld-error { font-size: 12px; color: #DC2626; margin-top: 6px; font-weight: 500; }

  .ld-row { display: flex; align-items: center; justify-content: space-between; margin: 2px 0 18px; }
  .ld-check { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; color: #374151; cursor: pointer; user-select: none; }
  .ld-check input { width: 16px; height: 16px; accent-color: #D71F2B; cursor: pointer; margin: 0; }
  .ld-link { background: none; border: none; padding: 0; color: #D71F2B; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .ld-link:hover { text-decoration: underline; }

  .ld-btn { width: 100%; height: 48px; border-radius: 10px; border: none; background: #D71F2B; color: #fff; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-family: inherit; box-shadow: 0 10px 22px -10px rgba(215,31,43,0.8); transition: background .15s, transform .15s; }
  .ld-btn:hover { background: #C0151F; }
  .ld-btn:active { transform: translateY(1px); }
  .ld-btn:disabled { background: #EF8A92; cursor: not-allowed; box-shadow: none; }
  .ld-btn svg { font-size: 18px; }

  .ld-divider { height: 1px; background: #EEF0F3; margin: 24px 0 18px; }
  .ld-helpbox { display: flex; align-items: center; gap: 12px; }
  .ld-helpbox-ico { width: 36px; height: 36px; border-radius: 10px; background: #FEECEE; color: #D71F2B; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .ld-helpbox-ico svg { font-size: 19px; }
  .ld-helpbox b { display: block; font-size: 13px; color: #111827; }
  .ld-helpbox span { display: block; font-size: 12px; color: #6B7280; margin-top: 1px; }
  .ld-signup { text-align: center; font-size: 12.5px; color: #6B7280; margin-top: 16px; }
  .ld-signup a { color: #D71F2B; font-weight: 700; text-decoration: none; }

  /* ── the OTP reset dialog ── */
  .ld-modal { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
  .ld-modal-card { background: #fff; border-radius: 14px; width: 100%; max-width: 380px; padding: 24px 22px; box-shadow: 0 24px 60px rgba(15,23,42,0.3); }
  .ld-modal-card h3 { margin: 0 0 6px; font-size: 18px; color: #0F172A; }
  .ld-modal-card p { margin: 0 0 14px; font-size: 13px; color: #6B7280; line-height: 1.5; }
  .ld-modal-card input { width: 100%; height: 44px; padding: 0 14px; border-radius: 10px; border: 1.5px solid #E5E7EB; font-size: 15px; outline: none; font-family: inherit; }
  .ld-modal-card input:focus { border-color: #D71F2B; }
  .ld-modal-msg { font-size: 12.5px; color: #D71F2B; margin-top: 8px; }
  .ld-modal-actions { display: flex; gap: 10px; margin-top: 16px; }
  .ld-btn-ghost { flex: 1; height: 44px; border-radius: 10px; border: 1.5px solid #E5E7EB; background: #fff; color: #374151; font-weight: 700; cursor: pointer; font-family: inherit; }
  .ld-btn-main { flex: 2; height: 44px; border-radius: 10px; border: none; background: #D71F2B; color: #fff; font-weight: 700; cursor: pointer; font-family: inherit; }
  .ld-btn-main:disabled { background: #EF8A92; cursor: not-allowed; }

  /* ── tablet / phone ── */
  @media (max-width: 980px) {
    .ld-root { grid-template-columns: 1fr; }
    .ld-hero { min-height: 0; padding: 28px 22px 26px; }
    .ld-eyebrow { margin-top: 22px; }
    .ld-h1 { font-size: 28px; }
    .ld-features { margin-top: 22px; gap: 12px 18px; }
    .ld-foot { margin-top: 22px; }
    .ld-side { min-height: 0; padding: 56px 20px 28px; }
  }
  @media (max-width: 600px) {
    .ld-features { display: none; }
    .ld-lead { font-size: 13.5px; }
    .ld-foot { display: none; }
    .ld-side { padding: 52px 16px 24px; }
    .ld-title { font-size: 23px; }
  }
`;

// The brand logo image (public/hoteldesk.png; the restaurant logo on a
// restaurant host). On the dark hero it sits on a white plate so the red
// mark keeps its colour; on the white card it is shown as-is.
function Logo({ onWhite }) {
  return (
    <div className={`ld-logo${onWhite ? ' ld-on-white' : ''}`}>
      <div
        style={{
          background: onWhite ? 'transparent' : '#fff',
          borderRadius: 10,
          padding: onWhite ? 0 : '7px 12px',
          display: 'inline-block',
        }}>
        <img
          src={BRAND.logo}
          alt={BRAND.name}
          style={{ height: onWhite ? 52 : 40, width: 'auto', maxWidth: 220, display: 'block' }}
        />
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: <BedOutlinedIcon />, title: 'Room Management', sub: 'Track availability & housekeeping' },
  { icon: <ReceiptLongOutlinedIcon />, title: 'Payments & Billing', sub: 'Invoice, ledger and settlements' },
  { icon: <EventAvailableOutlinedIcon />, title: 'Reservations', sub: 'Manage bookings and guest stays' },
  { icon: <BarChartOutlinedIcon />, title: 'Reports', sub: 'Real-time business insights' },
  { icon: <RestaurantOutlinedIcon />, title: 'Restaurant', sub: 'Integrated with Dine Master' },
  { icon: <DomainOutlinedIcon />, title: 'Multi-Property', sub: 'Manage multiple hotels' },
];

export default function Login() {
  const navigate = useNavigate();
  const { language, setLanguage, languages } = useLanguage();

  const remembered = readRemembered();
  const [form, setForm] = useState({ username: remembered?.username || '', password: '' });
  // Per-field validation messages: { companyName, username, password }. Shown
  // under the field itself rather than in an alert, so the operator can see
  // WHICH box is empty instead of reading a sentence about all three.
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [companyName, setCompanyName] = useState(remembered?.companyName || '');
  const [rememberMe, setRememberMe] = useState(!!remembered);
  const [langOpen, setLangOpen] = useState(false);
  // ── Email-OTP password reset ──
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = send OTP, 2 = verify
  const [resetUsername, setResetUsername] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPass, setResetNewPass] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetMaskedEmail, setResetMaskedEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  // Latest loaded company + the in-flight init() promise. The init API is
  // triggered when the operator LEAVES the company-code field (onBlur), not on
  // every keystroke — keystroke-firing raced the Login click and showed a false
  // "Company not loaded". These refs let handleLogin await whatever load is
  // already running instead of re-firing init on click.
  const companyRef = useRef(null);
  const loadInFlight = useRef(null);
  // Why the last company lookup came back empty, when it was NOT simply a code
  // that matched nothing: { code, detail }. Without it no internet read as
  // "This company code is not correct" and sent people checking a code that
  // was right all along.
  const initFailure = useRef(null);
  // Reload logic
  useEffect(() => {
    const hasReloaded = sessionStorage.getItem('loginReloaded');
    if (!hasReloaded) {
      sessionStorage.setItem('loginReloaded', 'true');
      window.location.reload();
    }
  }, []);

  // The page's typeface, loaded once (cached after the first visit).
  useEffect(() => {
    if (document.getElementById('inter-font')) return;
    const l = document.createElement('link');
    l.id = 'inter-font';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
  }, []);

  // Load Company Info
  useEffect(() => {
    loadCompany();
    // A remembered company code is looked up straight away, so the operator
    // only has to type the password.
    if (remembered?.companyName) loadCompany(remembered.companyName);
    // A failure kept on this phone from an earlier visit goes as soon as the
    // page opens - if the API is reachable now.
    flushLoginErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // init(): resolve the company from the typed code. Returns the loaded company
  // (or null on failure) and records the promise so a Login click can await it.
  const loadCompany = (clientValue) => {
    const client = (clientValue || '').trim();
    if (!client) return Promise.resolve(null);

    const promise = (async () => {
      initFailure.current = null;
      try {
        // SAVE CLIENT HERE
        localStorage.setItem('client', client);

        console.log('🔥 Final Client Used:', client);

        let res;
        try {
          res = await fetchWithTimeout(
            `${config.API_BASE_URL}/account/init?client=${encodeURIComponent(client)}`,
            { method: 'GET' },
          );
        } catch (netErr) {
          initFailure.current = {
            code: networkCodeFor(netErr),
            detail: `Company lookup: ${netErr?.name || 'Error'}: ${netErr?.message || netErr}`,
          };
          return null;
        }

        // The phone has just reached the API - anything waiting goes now.
        flushLoginErrors();

        if (!res.ok) {
          // The lookup broke on the server. That is not a wrong code.
          initFailure.current = {
            code: 'L09',
            detail: `Company lookup answered HTTP ${res.status}`,
          };
          return null;
        }

        const data = await res.json();
        const companyDetails = withRestaurantCompanyDetails(data);
        const propertyType = normalizePropertyType(data);

        console.log('✅ Company Loaded:', companyDetails);

        setCompany(companyDetails);
        companyRef.current = companyDetails;

        sessionStorage.setItem('companyId', companyDetails.id);
        if (propertyType) {
          sessionStorage.setItem('propertType', propertyType);
        }
        sessionStorage.setItem('companyDetails', JSON.stringify(companyDetails));
        return companyDetails;
      } catch (err) {
        console.error('❌ Init Error:', err);
        return null;
      }
    })();

    loadInFlight.current = promise;
    return promise;
  };

  // Guarantees a loaded company without re-firing init on click: reuse the
  // already-loaded one, else await the in-flight blur-triggered load, else (only
  // if the operator never left the field) do a single final load.
  const ensureCompany = async () => {
    if (companyRef.current?.id) return companyRef.current;
    if (loadInFlight.current) await loadInFlight.current;
    if (companyRef.current?.id) return companyRef.current;
    return loadCompany(companyName);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // The message goes the moment the operator starts fixing the field.
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  // "Remember me": keep (or forget) the company code and username. Never the
  // password.
  const persistRemember = () => {
    try {
      if (rememberMe)
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ companyName: companyName.trim(), username: form.username.trim() }));
      else localStorage.removeItem(REMEMBER_KEY);
    } catch {
      /* nowhere to keep it */
    }
  };

  const handleLogin = async () => {
    // Every empty box is named, all three at once - telling the operator about
    // one, then the next, is three round trips for one form.
    const nextErrors = {};
    if (!companyName.trim()) nextErrors.companyName = 'Please fill company code';
    if (!form.username.trim()) nextErrors.username = 'Please fill username';
    if (!form.password) nextErrors.password = 'Please fill password';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const comp = await ensureCompany();
    if (!comp?.id) {
      const typedCode = companyName.trim();
      const typedUser = form.username.trim();
      const failure = initFailure.current;

      // Could not reach the server at all - the code may be perfectly right.
      if (failure && NETWORK_MESSAGES[failure.code]) {
        const message = NETWORK_MESSAGES[failure.code];
        alert(`${message} (Code: ${failure.code})`);
        reportLoginError({
          errorCode: failure.code,
          companyCode: typedCode,
          username: typedUser,
          message,
          detail: failure.detail,
        });
        return;
      }

      // Reached the server, and the lookup itself broke there.
      if (failure?.code === 'L09') {
        const message =
          'We could not load your company right now. Please try again in a few minutes.';
        alert(`${message} (Code: L09)`);
        reportLoginError({
          errorCode: 'L09',
          companyCode: typedCode,
          username: typedUser,
          message,
          detail: failure.detail,
        });
        return;
      }

      // The server answered and no company matched: a WRONG code, not a page
      // that needs refreshing, and saying so sends people to the right fix.
      const message = 'Company code is not valid. Please check it.';
      setErrors({ companyName: `${message} (Code: L02)` });
      reportLoginError({
        errorCode: 'L02',
        companyCode: typedCode,
        username: typedUser,
        message,
      });
      return;
    }

    setLoading(true);

    try {
      // The request on its own, so "the server never answered" can be told
      // apart from everything else. Folded into the catch below, a phone with
      // no route to the API and a bug after a successful login both came out
      // as the same "Server error. Please try again later." - and a client's
      // screenshot of it told us nothing.
      let response;
      try {
        response = await fetchWithTimeout(`${config.API_BASE_URL}/account/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username,
            password: form.password,
            companyid: comp.id,
          }),
        });
      } catch (netErr) {
        console.error('❌ Login request did not reach the server:', netErr);
        // The server never saw this, so it cannot log it. Kept on the phone
        // and sent the next time the phone reaches the API.
        const code = networkCodeFor(netErr);
        const message = NETWORK_MESSAGES[code];
        alert(`${message} (Code: ${code})`);
        reportLoginError({
          errorCode: code,
          companyId: comp.id,
          companyCode: companyName.trim(),
          username: form.username.trim(),
          message,
          detail: `Login request: ${netErr?.name || 'Error'}: ${netErr?.message || netErr}`,
        });
        setLoading(false);
        return;
      }

      // An answer came back, so the phone can reach the API - send anything
      // that was waiting from an earlier failure.
      flushLoginErrors();

      if (!response.ok) {
        // The API's own words and code (L01-L08, see AccountController), so a
        // screenshot says which refusal it was. The API has already logged
        // it. Written to be safe to show: no technical detail.
        // The fallbacks only cover an API that has not been redeployed yet.
        const err = await response.json().catch(() => null);
        const message =
          err?.message ||
          (err?.accountDeleted
            ? 'Your account is deleted.'
            : response.status >= 500
              ? 'We could not sign you in right now. Please try again in a few minutes.'
              : 'Wrong username or password.');
        const code = err?.errorCode || `HTTP ${response.status}`;
        const ref = err?.reference ? `, Ref: ${err.reference}` : '';
        alert(`${message} (Code: ${code}${ref})`);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.message === 'Login successful') {
        persistRemember();
        sessionStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.setItem('username', data.user.username);
        sessionStorage.setItem('userId', data.user.id);
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('permissions', JSON.stringify(data.permisioon));
        const rbacCodes = data.permissionCodes ?? data.PermissionCodes;
        if (Array.isArray(rbacCodes) && rbacCodes.length > 0) {
          sessionStorage.setItem('permissionCodes', JSON.stringify(rbacCodes));
        } else {
          sessionStorage.removeItem('permissionCodes');
        }
        notifyRbacUpdated();

        // One-time direct-login URL returned by the login API (single-use link).
        if (data.directLoginUrl) {
          sessionStorage.setItem('directLoginUrl', data.directLoginUrl);
        } else {
          sessionStorage.removeItem('directLoginUrl');
        }

        console.log('✅ Login Success:', data);

        // No `pendingDashboardRefresh` flag any more. It used to ask the
        // dashboard to reload the whole application once after a login, to work
        // around screens that read the session at module level. Code splitting
        // and CompanyGate removed that need - see the note in
        // DashboardSwitcher.js. Everything this login writes is written ABOVE
        // this line, before navigate() below, so the screens that follow already
        // find a complete session.

        // ── First-time setup check ──────────────────────────
        // Hotel (default): no properties → HotelSetupWizard at /setup.
        // Restaurant (propertType R from account/init): incomplete → same route;
        // HotelSetupWizard delegates to RestaurantSetupWizard when isRestaurant is set.
        const isRestaurant = isRestaurantProperty(comp);
        try {
          const hotelRes = await fetch(
            `${config.API_BASE_URL}/Home/GetHotels/${encodeURIComponent(comp.id)}`,
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          if (hotelRes.ok) {
            const raw = await hotelRes.json();
            const hotels = extractHotels(raw);
            const hasProperties = hotels.length > 0;
            if (!hasProperties) {
              localStorage.removeItem('hotelSetupDone');
              navigate('/setup');
              return;
            }
          } else if (isRestaurant) {
            localStorage.removeItem('hotelSetupDone');
            navigate('/setup');
            return;
          }
        } catch (e) {
          if (isRestaurant) {
            console.warn(
              'Restaurant setup check failed, opening setup wizard:',
              e,
            );
            localStorage.removeItem('hotelSetupDone');
            navigate('/setup');
            return;
          }
          console.warn('Hotel check failed, proceeding to dashboard:', e);
        }
        // ────────────────────────────────────────────────────

        // ── Subscription access-check (additive; fails open) ──
        // A company can use HMS only if a current subscription exists and is
        // not expired. On any error we proceed normally so login is never
        // blocked by the check itself.
        try {
          const subRes = await fetch(
            `${config.API_BASE_URL}/Subscription/status/${encodeURIComponent(comp.id)}`,
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          if (subRes.ok) {
            const sub = await subRes.json();
            sessionStorage.setItem('subscriptionStatus', JSON.stringify(sub));
            if (sub && sub.canAccess === false) {
              // Expired / none → send them to renew instead of the dashboard.
              localStorage.setItem('hotelSetupDone', 'true');
              navigate('/admin/Subscription');
              return;
            }
          }
        } catch (e) {
          console.warn('Subscription check failed, proceeding to dashboard:', e);
        }

        localStorage.setItem('hotelSetupDone', 'true');
        navigate('/admin');
      } else {
        alert('Wrong username or password');
      }
    } catch (error) {
      // Only reached AFTER the server answered: the login response could not be
      // read, or something failed while opening the app. L06, so it is never
      // mistaken for L00 (no answer at all). The browser's own error goes to
      // the log - never onto the screen.
      console.error('❌ Login Error:', error);
      const message =
        'Signed in, but the app could not open. Please refresh the page and try again.';
      alert(`${message} (Code: L06)`);
      reportLoginError({
        errorCode: 'L06',
        companyId: comp?.id,
        companyCode: companyName.trim(),
        username: form.username.trim(),
        message,
        detail: `${error?.name || 'Error'}: ${error?.message || error}\n${String(
          error?.stack || '',
        ).slice(0, 1500)}`,
      });
    }

    setLoading(false);
  };

  // Forgot Password Handler
  const handleForgotPassword = async () => {
    if (!form.username) {
      alert('Please enter your username first');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      alert('Company not loaded. Please refresh.');
      return;
    }

    setForgotLoading(true);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/account/forgotpassword`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username,
            companyid: comp.id,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        alert(
          data.message ||
            'Password reset link has been sent to your registered email.',
        );
      } else {
        alert(data.message || 'Failed to process forgot password request.');
      }
    } catch (error) {
      console.error('❌ Forgot Password Error:', error);
      alert('Something went wrong. Please try again later.');
    }

    setForgotLoading(false);
  };

  // ── Email-OTP password reset handlers ──
  const openReset = () => {
    setResetUsername(form.username || '');
    setResetStep(1);
    setResetOtp('');
    setResetNewPass('');
    setResetMsg('');
    setResetMaskedEmail('');
    setResetOpen(true);
  };

  const sendResetOtp = async () => {
    if (!resetUsername.trim()) {
      setResetMsg('Please enter your username.');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      setResetMsg('Company not loaded. Please refresh.');
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/passwordreset/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: resetUsername.trim(),
          companyId: comp.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetMsg(data.error || 'Failed to send OTP.');
        return;
      }
      setResetMaskedEmail(data.email || '');
      setResetStep(2);
      setResetMsg('OTP sent to your registered email.');
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const submitReset = async () => {
    if (!resetOtp.trim() || !resetNewPass) {
      setResetMsg('Enter the OTP and a new password.');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      setResetMsg('Company not loaded. Please refresh.');
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/passwordreset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: resetUsername.trim(),
          companyId: comp.id,
          otp: resetOtp.trim(),
          newPassword: resetNewPass,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetMsg(data.error || 'Failed to reset password.');
        return;
      }
      alert('Password reset successful. Please log in with your new password.');
      setResetOpen(false);
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  // Enter in any box signs in, as the old page's button did.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !loading) handleLogin();
  };

  const currentLang = (languages || []).find((l) => l.code === language) || { code: 'en', native: 'English', label: 'English' };

  return (
    <div className='ld-root'>
      <style>{STYLES}</style>

      {/* ═══════ left: the hero ═══════ */}
      <aside className='ld-hero'>
        {/* Drop the hotel photo at public/login-hero.jpg; until then the night
            skyline below stands in. A missing file hides itself. */}
        <img className='ld-hero-img' src='/login-hero.jpg' alt='' onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div className='ld-hero-skyline' />
        <div className='ld-hero-shade' />

        <div>
          <Logo />
          <div className='ld-eyebrow'>{BRAND.isRestaurant ? 'Restaurant management system' : 'Hotel management system'}</div>
          <h1 className='ld-h1'>
            {BRAND.isRestaurant ? 'Restaurant Operations' : 'Hotel Operations'}
            <br />
            Made <span className='ld-accent'>Simple.</span>
          </h1>
          <p className='ld-lead'>
            Manage reservations, rooms, restaurant, guest services, payments and more &mdash; all in one place.
          </p>
          <div className='ld-features'>
            {FEATURES.map((f) => (
              <div className='ld-feature' key={f.title}>
                <div className='ld-feature-ico'>{f.icon}</div>
                <div>
                  <b>{f.title}</b>
                  <span>{f.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='ld-foot'>
          Powered by <b>GoERP.live</b>
          <br />
          A Product of GoERP Technologies Pvt. Ltd.
        </div>
      </aside>

      {/* ═══════ right: sign in ═══════ */}
      <main className='ld-side'>
        <div className='ld-lang'>
          <button type='button' className='ld-lang-btn' onClick={() => setLangOpen((v) => !v)} aria-haspopup='listbox' aria-expanded={langOpen}>
            <LanguageOutlinedIcon /> {currentLang.native || currentLang.label} <KeyboardArrowDownIcon />
          </button>
          {langOpen && (
            <div className='ld-lang-menu' role='listbox'>
              {(languages || []).map((l) => (
                <div
                  key={l.code}
                  role='option'
                  aria-selected={l.code === language}
                  className={`ld-lang-item${l.code === language ? ' ld-on' : ''}`}
                  onClick={() => { setLanguage(l.code); setLangOpen(false); }}>
                  <span>{l.native}</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>{l.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='ld-card'>
          <Logo onWhite />
          <h2 className='ld-title'>Welcome back</h2>
          <p className='ld-sub'>Sign in to your {BRAND.name} account</p>

          {/* Company Code */}
          <div className='ld-field'>
            <label className='ld-label' htmlFor='ld-company'>Company Code <InfoOutlinedIcon /></label>
            <div className={`ld-input${errors.companyName ? ' ld-err' : ''}`}>
              <ApartmentOutlinedIcon />
              <input
                id='ld-company'
                type='text'
                value={companyName}
                autoComplete='organization'
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setErrors((prev) => ({ ...prev, companyName: '' }));
                }}
                placeholder='Enter company code'
                onKeyDown={onKeyDown}
                onBlur={() => {
                  // Run init() when the operator leaves the field (e.g. tabs to
                  // Username or clicks Login) — once, with the full code.
                  loadCompany(companyName);
                }}
              />
            </div>
            {errors.companyName ? (
              <div className='ld-error'>{errors.companyName}</div>
            ) : (
              <div className='ld-help'>Provided by your administrator</div>
            )}
          </div>

          {/* Username */}
          <div className='ld-field'>
            <label className='ld-label' htmlFor='ld-username'>Username</label>
            <div className={`ld-input${errors.username ? ' ld-err' : ''}`}>
              <PersonOutlineIcon />
              <input
                id='ld-username'
                type='text'
                name='username'
                autoComplete='username'
                value={form.username}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                placeholder='Enter username'
              />
            </div>
            {errors.username && <div className='ld-error'>{errors.username}</div>}
          </div>

          {/* Password with Show/Hide */}
          <div className='ld-field'>
            <label className='ld-label' htmlFor='ld-password'>Password</label>
            <div className={`ld-input${errors.password ? ' ld-err' : ''}`}>
              <LockOutlinedIcon />
              <input
                id='ld-password'
                type={showPassword ? 'text' : 'password'}
                name='password'
                autoComplete='current-password'
                value={form.password}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                placeholder='Enter password'
              />
              <button type='button' className='ld-eye' onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </button>
            </div>
            {errors.password && <div className='ld-error'>{errors.password}</div>}
          </div>

          {/* Remember me / Forgot password — opens email-OTP reset */}
          <div className='ld-row'>
            <label className='ld-check'>
              <input type='checkbox' checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              Remember me
            </label>
            <button type='button' className='ld-link' onClick={openReset}>Forgot password?</button>
          </div>

          {/* Login Button */}
          <button type='button' className='ld-btn' onClick={handleLogin} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'} {!loading && <ArrowForwardIcon />}
          </button>

          <div className='ld-divider' />
          <div className='ld-helpbox'>
            <div className='ld-helpbox-ico'><HeadsetMicOutlinedIcon /></div>
            <div>
              <b>Need help?</b>
              <span>Contact your administrator</span>
            </div>
          </div>
          <div className='ld-signup'>
            Don&apos;t have an account?{' '}
            <a href='https://signup.goerp.live/Account/Signup' target='_blank' rel='noopener noreferrer'>Sign Up</a>
          </div>
        </div>
      </main>

      {/* ── Email-OTP password reset modal ── */}
      {resetOpen && (
        <div className='ld-modal' onClick={(e) => { if (e.target === e.currentTarget && !resetLoading) setResetOpen(false); }}>
          <div className='ld-modal-card'>
            <h3>Reset Password</h3>
            <p>
              {resetStep === 1
                ? 'Enter your username. An OTP will be sent to your registered email.'
                : `Enter the OTP${resetMaskedEmail ? ` sent to ${resetMaskedEmail}` : ''} and your new password.`}
            </p>

            {resetStep === 1 ? (
              <input
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
                placeholder='Username'
              />
            ) : (
              <>
                <input
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value)}
                  placeholder='6-digit OTP'
                  inputMode='numeric'
                />
                <input
                  type='password'
                  value={resetNewPass}
                  onChange={(e) => setResetNewPass(e.target.value)}
                  placeholder='New password'
                  style={{ marginTop: 10 }}
                />
              </>
            )}

            {resetMsg && <div className='ld-modal-msg'>{resetMsg}</div>}

            <div className='ld-modal-actions'>
              <button type='button' className='ld-btn-ghost' onClick={() => setResetOpen(false)}>Cancel</button>
              <button type='button' className='ld-btn-main' disabled={resetLoading} onClick={resetStep === 1 ? sendResetOtp : submitReset}>
                {resetLoading ? 'Please wait...' : resetStep === 1 ? 'Send OTP' : 'Reset Password'}
              </button>
            </div>

            {resetStep === 2 && (
              <button type='button' className='ld-link' onClick={sendResetOtp} disabled={resetLoading} style={{ marginTop: 12, fontSize: 12.5 }}>
                Resend OTP
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
